import { DataType } from '@/types';
import { apiUrl, CHAT_METADATA_KEY, CONVERSATION_LIST_KEY, fetchWithAuth } from '@/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/shadcn/button';
import { Checkbox } from 'components/shadcn/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/shadcn/dialog';
import { Input } from 'components/shadcn/input';
import { Label } from 'components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/shadcn/select';
import { Plus } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function CreateConversationField({
  chatId,
  fieldNames,
}: {
  chatId: string;
  fieldNames: string[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [dataType, setDataType] = useState<string>(DataType.STRING);
  const [extractWithAi, setExtractWithAi] = useState(false);
  const mutation = useMutation({
    mutationFn: async (field: {
      name: string;
      description: string;
      dataType: string;
      extractWithAi: boolean;
    }) => {
      const { extractWithAi, ...definition } = field;
      if (fieldNames.includes(definition.name)) {
        throw new Error(t('dashboard.conversations.sidebar.duplicateField'));
      }
      const response = await fetchWithAuth(`${apiUrl}/chat-metadata/${chatId}`, {
        method: 'PUT',
        body: JSON.stringify({
          [extractWithAi ? 'dataToExtract' : 'metadataFields']: { create: definition },
        }),
      });
      if (!response.ok) {
        throw new Error(
          t(
            `dashboard.conversations.sidebar.${response.status === 409 ? 'duplicateField' : 'createFieldError'}`,
          ),
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONVERSATION_LIST_KEY, chatId] });
      queryClient.invalidateQueries({ queryKey: [CHAT_METADATA_KEY] });
      setOpen(false);
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    mutation.mutate({
      name: String(values.get('name')).trim(),
      description: String(values.get('description')).trim(),
      dataType,
      extractWithAi,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        setOpen(next);
        if (next) {
          mutation.reset();
          setDataType(DataType.STRING);
          setExtractWithAi(false);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="size-4" aria-hidden="true" />
          {t('dashboard.metadataFields.addField')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('dashboard.metadataFields.addField')}</DialogTitle>
          <DialogDescription>
            {t('dashboard.conversations.sidebar.createFieldDescription')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-field-name">{t('dashboard.metadataFields.fieldName')}</Label>
            <Input
              id="new-field-name"
              name="name"
              required
              pattern=".*\S.*"
              disabled={mutation.isPending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-field-description">
              {t('dashboard.metadataFields.description')}
            </Label>
            <Input
              id="new-field-description"
              name="description"
              pattern=".*\S.*"
              required
              disabled={mutation.isPending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-field-type">{t('dashboard.metadataFields.dataType')}</Label>
            <Select value={dataType} onValueChange={setDataType} disabled={mutation.isPending}>
              <SelectTrigger id="new-field-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(DataType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="new-field-ai"
              checked={extractWithAi}
              onCheckedChange={(checked) => setExtractWithAi(checked === true)}
              disabled={mutation.isPending}
              aria-describedby="new-field-ai-help"
            />
            <div className="space-y-1">
              <Label htmlFor="new-field-ai">
                {t('dashboard.conversations.sidebar.extractWithAi')}
              </Label>
              <p id="new-field-ai-help" className="text-sm text-muted-foreground">
                {t('dashboard.conversations.sidebar.extractWithAiHelp')}
              </p>
            </div>
          </div>
          {mutation.error && (
            <p role="alert" className="text-sm text-destructive">
              {mutation.error.message}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              {t('ui.close')}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {t('dashboard.metadataFields.addField')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
